import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import SigPad from 'signature_pad';
import {debounce} from 'throttle-debounce';

class SignaturePad extends PureComponent {

    static displayName = 'react-signature-pad-wrapper';

    static propTypes = {
        width: PropTypes.number,
        height: PropTypes.number,
        options: PropTypes.object,
        redrawOnResize: PropTypes.bool.isRequired,
        debounceInterval: PropTypes.number.isRequired,
        canvasProps: PropTypes.object
    }

    static defaultProps = {
        redrawOnResize: false,
        debounceInterval: 150
    }

    constructor(props) {
        super(props);

        this.state = {canvasWidth: 0, canvasHeight: 0};

        this._callResizeHandler = debounce(
            this.props.debounceInterval,
            this.handleResize.bind(this)
        );
    }

    componentDidMount() {
        if (this._canvas) {
            if (!this.props.width || !this.props.height) {
                this._canvas.style.width = '100%';
            }
            this.scaleCanvas();

            if (!this.props.width || !this.props.height) {
                window.addEventListener('resize', this._callResizeHandler);
            }

            this._signaturePad = new SigPad(this._canvas, this.props.options);
            if (this.props.onEnd) {
                this._signaturePad.onEnd = () => {
                    this.props.onEnd(this._signaturePad);
                };
            }
        }
    }

    componentWillUnmount() {
        if (!this.props.width || !this.props.height) {
            window.removeEventListener('resize', this._callResizeHandler);
        }

        this._signaturePad.off();
    }

    /**
     * Get the original signature_pad instance.
     */
    get instance() {
        return this._signaturePad;
    }

    get canvas() {
        return this._canvas;
    }

    set dotSize(dotSize) {
        this._signaturePad.dotSize = dotSize;
    }

    get dotSize() {
        return this._signaturePad.dotSize;
    }

    set minWidth(minWidth) {
        this._signaturePad.minWidth = minWidth;
    }

    get minWidth() {
        return this._signaturePad.minWidth;
    }

    set maxWidth(maxWidth) {
        this._signaturePad.maxWidth = maxWidth;
    }

    get maxWidth() {
        return this._signaturePad.maxWidth;
    }

    set throttle(throttle) {
        this._signaturePad.throttle = throttle;
    }

    get throttle() {
        return this._signaturePad.throttle;
    }

    set backgroundColor(color) {
        this._signaturePad.backgroundColor = color;
    }

    get backgroundColor() {
        return this._signaturePad.backgroundColor;
    }

    set penColor(color) {
        this._signaturePad.penColor = color;
    }

    get penColor() {
        return this._signaturePad.penColor;
    }

    set velocityFilterWeight(weight) {
        this._signaturePad.velocityFilterWeight = weight;
    }

    get velocityFilterWeight() {
        return this._signaturePad.velocityFilterWeight;
    }

    set onBegin(fn) {
        if (!(fn && typeof fn === 'function')) {
            throw new Error('Invalid argument passed to onBegin()');
        }

        this._signaturePad.onBegin = fn;
    }

    set onEnd(fn) {
        if (!(fn && typeof fn === 'function')) {
            throw new Error('Invalid argument passed to onEnd()');
        }

        this._signaturePad.onEnd = fn;
    }

    isEmpty() {
        return this._signaturePad.isEmpty();
    }

    clear() {
        this._signaturePad.clear();
    }

    fromDataURL(base64String, options) {
        this._signaturePad.fromDataURL(base64String, options);
    }


    toSVG() {
        let pad = this._signaturePad;
        const pointGroups = pad._data;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const minX = 0;
        const minY = 0;
        const maxX = pad.canvas.width / ratio;
        const maxY = pad.canvas.height / ratio;
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', pad.canvas.width.toString());
        svg.setAttribute('height', pad.canvas.height.toString());

        pad._fromData(pointGroups, ({
                                         color,
                                         curve
                                     }) => {
            const path = document.createElement('path');

            if (!isNaN(curve.control1.x) && !isNaN(curve.control1.y) && !isNaN(curve.control2.x) && !isNaN(curve.control2.y)) {
                const attr = "M ".concat(curve.startPoint.x.toFixed(3), ",").concat(curve.startPoint.y.toFixed(3), " ") + "C ".concat(curve.control1.x.toFixed(3), ",").concat(curve.control1.y.toFixed(3), " ") + "".concat(curve.control2.x.toFixed(3), ",").concat(curve.control2.y.toFixed(3), " ") + "".concat(curve.endPoint.x.toFixed(3), ",").concat(curve.endPoint.y.toFixed(3));
                path.setAttribute('d', attr);
                path.setAttribute('stroke-width', (curve.endWidth * 2.25).toFixed(3));
                path.setAttribute('stroke', color);
                path.setAttribute('fill', 'none');
                path.setAttribute('stroke-linecap', 'round');
                svg.appendChild(path);
            }
        }, ({
                color,
                point
            }) => {
            const circle = document.createElement('circle');
            const dotSize = typeof pad.dotSize === 'function' ? pad.dotSize() : pad.dotSize;
            circle.setAttribute('r', dotSize.toString());
            circle.setAttribute('cx', point.x.toString());
            circle.setAttribute('cy', point.y.toString());
            circle.setAttribute('fill', color);
            svg.appendChild(circle);
        });

        const prefix = 'data:image/svg+xml;base64,';
        const header = '<svg' + ' xmlns="http://www.w3.org/2000/svg"' + ' xmlns:xlink="http://www.w3.org/1999/xlink"' + " viewBox=\"".concat(minX, " ").concat(minY, " ").concat(pad.canvas.width.toString(), " ").concat(pad.canvas.height.toString(), "\"") + " width=\"".concat(pad.canvas.width.toString(), "\"") + " height=\"".concat(pad.canvas.height.toString(), "\"") + '>';
        let body = svg.innerHTML;

        if (body === undefined) {
            const dummy = document.createElement('dummy');
            const nodes = svg.childNodes;
            dummy.innerHTML = '';

            for (let i = 0; i < nodes.length; i += 1) {
                dummy.appendChild(nodes[i].cloneNode(true));
            }

            body = dummy.innerHTML;
        }

        const footer = '</svg>';
        const data = header + body + footer;
        return prefix + btoa(data);
    }

    toDataURL(mime) {
        return this._signaturePad.toDataURL(mime);
    }

    fromData(data) {
        this._signaturePad.fromData(data);
    }

    toData() {
        return this._signaturePad.toData();
    }

    off() {
        this._signaturePad.off();
    }

    on() {
        this._signaturePad.on();
    }

    handleResize() {
        this.scaleCanvas();
    }

    scaleCanvas() {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const width = (this.props.width || this._canvas.offsetWidth);
        const height = (this.props.height || this._canvas.offsetHeight);

        // Avoid needlessly setting height/width if dimensions haven't changed
        const {canvasWidth, canvasHeight} = this.state;
        if (width === canvasWidth && height === canvasHeight) return;

        let data;
        if (this.props.redrawOnResize && this._signaturePad) {
            data = this._signaturePad.toDataURL();
        }

        this._canvas.width = width;
        this._canvas.height = height;

        this.setState({canvasWidth: width, canvasHeight: height});

        const ctx = this._canvas.getContext('2d');
        //ctx.scale(ratio, ratio);

        if (this.props.redrawOnResize && this._signaturePad) {
            this._signaturePad.fromDataURL(data);
        } else if (this._signaturePad) {
            this._signaturePad.clear();
        }
    }

    render() {
        const {canvasProps} = this.props;

        return (
            <canvas
                ref={ref => this._canvas = ref}
                {...canvasProps} />
        );
    }

}

export default SignaturePad;